import SentimentSatisfiedOutlinedIcon from "@mui/icons-material/SentimentSatisfiedOutlined";
import { IconButton, Popover, SxProps, TextField, Theme } from "@mui/material";
import { MouseEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { EMOJIS } from "./emojiData";
import {
  filterEmojis,
  getRecentEmojis,
  groupEmojis,
  persistRecentEmoji,
  resolveRecentEmojis,
} from "./emojiPickerUtils";
import {
  EmojiPickerButton,
  EmojiPickerContent,
  EmojiPickerGrid,
  EmojiPickerGroup,
  EmojiPickerList,
  EmojiPickerSectionTitle,
} from "./styles";
import { EMOJI_GROUP, EmojiItem } from "./types";

type Props = {
  onSelect: (emoji: string) => void;
  disabled?: boolean;
  buttonSx?: SxProps<Theme>;
};

type EmojiSectionProps = {
  title: string;
  emojis: EmojiItem[];
  onSelect: (emoji: string) => void;
};

const EmojiSection = ({ title, emojis, onSelect }: EmojiSectionProps) => (
  <>
    <EmojiPickerSectionTitle>{title}</EmojiPickerSectionTitle>
    <EmojiPickerGroup>
      <EmojiPickerGrid>
        {emojis.map((item) => (
          <EmojiPickerButton
            key={`${item.group}-${item.emoji}`}
            aria-label={item.name}
            onClick={() => onSelect(item.emoji)}
          >
            {item.emoji}
          </EmojiPickerButton>
        ))}
      </EmojiPickerGrid>
    </EmojiPickerGroup>
  </>
);

const EmojiPicker = ({ onSelect, disabled, buttonSx }: Props) => {
  const { t } = useTranslation("common");
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [query, setQuery] = useState("");
  const [recents, setRecents] = useState<string[]>(() => getRecentEmojis());

  const open = Boolean(anchorEl);
  const filteredEmojis = useMemo(() => filterEmojis(EMOJIS, query), [query]);
  const sections = useMemo(() => groupEmojis(filteredEmojis), [filteredEmojis]);
  const recentItems = useMemo(
    () => (query.trim() ? [] : resolveRecentEmojis(recents, EMOJIS)),
    [query, recents],
  );

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setRecents(getRecentEmojis());
  };

  const handleClose = () => {
    setAnchorEl(null);
    setQuery("");
  };

  const handleSelect = (emoji: string) => {
    setRecents(persistRecentEmoji(emoji));
    onSelect(emoji);
    handleClose();
  };

  return (
    <>
      <IconButton
        aria-label={t("addEmoji")}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
        onClick={handleOpen}
        sx={buttonSx}
        data-testid="emoji-picker-button"
      >
        <SentimentSatisfiedOutlinedIcon sx={{ fontSize: 20 }} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        disableRestoreFocus
        slotProps={{
          paper: { sx: { overflow: "hidden" } },
        }}
      >
        <EmojiPickerContent data-testid="emoji-picker-popover">
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("searchEmoji")}
            inputProps={{
              "aria-label": t("searchEmoji"),
              autoComplete: "off",
            }}
          />
          <EmojiPickerList>
            {recentItems.length > 0 && (
              <EmojiSection
                title={t("emojiGroupRecent")}
                emojis={recentItems}
                onSelect={handleSelect}
              />
            )}
            {sections.map((section) => (
              <EmojiSection
                key={section.group}
                title={
                  section.group === EMOJI_GROUP.SMILEYS
                    ? t("emojiGroupSmileys")
                    : t("emojiGroupGestures")
                }
                emojis={section.emojis}
                onSelect={handleSelect}
              />
            ))}
            {filteredEmojis.length === 0 && (
              <EmojiPickerSectionTitle>
                {t("emojiNoResults")}
              </EmojiPickerSectionTitle>
            )}
          </EmojiPickerList>
        </EmojiPickerContent>
      </Popover>
    </>
  );
};

export default EmojiPicker;
